import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { tenantsOf, type McpTenantList } from "../../api/mcp";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { ALL_TABS } from "../../nav/sections";
import { productById } from "../../products/registry";

interface Hit {
  label: string;
  hint: string;
  to: string;
}

/**
 * ⌘K — jump to a page, or to a merchant by any identifier an operator has.
 *
 * The point is the merchant lookup. During an incident someone pastes a
 * reference, a name, or an owner's email from a ticket, and resolving which of
 * those it is should be the console's job, not theirs.
 *
 * `business_id` is NOT searchable, even though the design's palette matches on
 * it. It is absent from every type in api/mcp.ts on purpose — it is an
 * internal UUID, ops know merchants by reference, and adding it here would
 * reintroduce the one identifier the console deliberately never shows.
 *
 * A merchant NOT in the list is still reachable: the list only holds merchants
 * seen in the last 365 days, so the palette always offers "open by reference"
 * for whatever was typed. A dormant merchant can still be blocked, and a
 * palette that only offered what it could enumerate would hide that.
 */
export function Palette({ config }: { config: AppConfig }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Only fetched once the palette is opened — the shell should not pull the
  // whole tenant list on every page load for a feature nobody may use.
  const tenants = useResource<McpTenantList>(config, open ? "/mcp/admin/tenants" : null, [open]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /**
   * Focus goes in on open and BACK OUT on close.
   *
   * Without the restore, dismissing the palette left focus on `<body>`, so a
   * keyboard user's next Tab started from the top of the document rather than
   * from the control they opened it with.
   */
  const returnTo = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      returnTo.current = document.activeElement as HTMLElement | null;
      inputRef.current?.focus();
    } else {
      setQ("");
      returnTo.current?.focus?.();
      returnTo.current = null;
    }
  }, [open]);

  const hits = useMemo<Hit[]>(() => {
    const term = q.trim().toLowerCase();
    // Matched on the PRODUCT too, so typing "labeeb" finds every surface that
    // product owns. During an incident the thing someone knows is which
    // product is misbehaving, not what this console decided to call the tab.
    const pages: Hit[] = ALL_TABS.filter((t) => {
      if (!term) return true;
      const names = t.products.map((id) => productById(id)?.label ?? id).join(" ");
      return `${t.label} ${names}`.toLowerCase().includes(term);
    }).map((t) => ({
      label: t.label,
      hint: `page · ${t.products.map((id) => productById(id)?.label ?? id).join(" + ")}${
        t.comingSoon ? " · coming soon" : ""
      }`,
      to: t.to,
    }));

    if (!term) return pages.slice(0, 8);

    const list = tenants.state.kind === "ready" ? tenantsOf(tenants.state.data) : [];
    const merchants: Hit[] = list
      .filter((m) =>
        `${m.business_reference} ${m.business_name} ${m.email}`.toLowerCase().includes(term),
      )
      .slice(0, 6)
      .map((m) => ({
        label: m.business_name,
        hint: `${m.business_reference} · ${m.email}`,
        to: `/tenants/${encodeURIComponent(m.business_reference)}`,
      }));

    // Always offered, never conditional on the list containing it.
    const direct: Hit = {
      label: `Open merchant ${q.trim()}`,
      hint: "by reference — works for merchants not listed",
      to: `/tenants/${encodeURIComponent(q.trim())}`,
    };

    return [...merchants, ...pages.slice(0, 4), direct];
  }, [q, tenants.state]);

  /**
   * Enter is held until the merchant list has landed.
   *
   * Merchants rank above pages, so before the fetch resolves `hits[0]` is a
   * PAGE and after it resolves it may be a merchant — the same keystrokes
   * meaning two different destinations depending on timing. Rather than race,
   * a typed term waits: the list is one request, opened on ⌘K, and this only
   * withholds Enter for as long as it is in flight.
   */
  const resolving = q.trim().length > 0 && tenants.state.kind === "loading";

  function go(to: string) {
    setOpen(false);
    navigate(to);
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="w-full max-w-xl overflow-hidden rounded-md border border-slate-950/10 dark:border-white/10 bg-white dark:bg-slate-900 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="search"
          value={q}
          // The placeholder is not a label — the console's own rule, which
          // SearchField in ui.tsx follows and this input did not.
          aria-label="Search pages, or a merchant by reference, name or email"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            if (resolving || !hits[0]) return;
            go(hits[0].to);
          }}
          placeholder="Search pages, or a merchant by reference, name or email…"
          className="w-full border-b border-slate-950/10 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-3 text-sm outline-hidden"
        />
        <ul className="max-h-80 overflow-y-auto">
          {resolving && (
            <li className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
              Looking up merchants…
            </li>
          )}
          {hits.map((h) => (
            <li key={`${h.to}-${h.label}`}>
              <button
                onClick={() => go(h.to)}
                className="flex w-full items-baseline justify-between gap-3 px-4 py-2 text-left text-sm hover:bg-slate-950/[2.5%] dark:hover:bg-white/5"
              >
                <span>{h.label}</span>
                <span className="shrink-0 text-xs text-slate-400 dark:text-slate-500">{h.hint}</span>
              </button>
            </li>
          ))}
          {hits.length === 0 && (
            <li className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">Nothing matches that.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
